import "@mantine/tiptap/styles.css";

import {
  BusinessPicker,
  CurrencyPicker,
  FeatureWrap,
  InsurerPicker,
  ReInsurerPicker,
  RegularSelectInput,
  RichEditor,
  Show,
  SwitchInput,
  TextInput,
} from "components/core";
import { FC, useEffect } from "react";

import { FormikProps } from "formik";
import _ from "lodash";
import numeral from "numeral";

export interface OfferFormProps {
  form: FormikProps<any>;
}

const OfferForm: FC<OfferFormProps> = ({ form }) => {
  const details = JSON.parse(
    _.get(form.values, "class_of_business_id.business_details") || "[]"
  );

  useEffect(() => {
    form.setFieldValue(
      "offer_details",
      form?.values?.old_class_of_business_id &&
        form?.values?.old_class_of_business_id ===
        form?.values?.class_of_business_id?.class_of_business_id
        ? form?.values?.old_offer_detail
        : JSON.parse(
          _.get(form.values, "class_of_business_id.business_details") || "[]"
        ).map((detail: any) => ({ keydetail: detail.keydetail, value: "" }))
    );
  }, [form.values.class_of_business_id]);

  useEffect(() => {
    if (form?.values?.premium && form.values?.sum_insured) {
      form.setFieldValue(
        "rate",
        numeral(
          _.divide(form.values.premium, form.values.sum_insured),
        ).format("0.00000000")
      );
    }
  }, [form.values]);

  return (
    <div className="space-y-6 divide-y divide-gray-200">
      {/* {JSON.stringify(form.values, null, 2)} */}
      <FeatureWrap domains={["KEK"]}>
        <div>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <RegularSelectInput
                id="business_state"
                label="Business State"
                placeholder="e.g. Select Business State"
                required={true}
                options={[
                  { label: "New Business", value: "NEW" },
                  { label: "Old Business", value: "OLD" },
                ]}
                {...form}
              />
            </div>
            <div className="col-span-2">
              <SwitchInput
                id="createForRetroCedent"
                label="Create Offer for Retrocedent"
                description="Toggle to create an offer for the retrocedent."
                {...form}
              />
            </div>
          </div>
        </div>
      </FeatureWrap>
      <div>
        <span className="text-xs font-light">Offer</span>
        <div className="grid grid-cols-2 gap-2">
          <FeatureWrap domains={["KEK"]} condition={form.values.createForRetroCedent}>
            <div className="">
              <ReInsurerPicker
                id="reinsurer_id"
                label="Retrocedent"
                placeholder="e.g. Select Retrocedent"
                rawId
                required={true}
                {...form}
              />
            </div>
          </FeatureWrap>
          <div className="">
            <InsurerPicker
              id="insurer_id"
              label={form.values.createForRetroCedent ? "Reinsured" : "Insurer"}
              placeholder="e.g. Select Insurer"
              rawId
              required={true}
              {...form}
            />
          </div>
          <div className={form.values.createForRetroCedent ? "col-span-2" : ""}>
            <BusinessPicker
              id="class_of_business_id"
              label="Class of business"
              placeholder="e.g. Select class of business"
              required={true}
              {...form}
            />
          </div>
        </div>
      </div>
      {details?.length > 0 && (
        <>
          <div className="pt-6">
            <span className="text-xs font-light">Business Details</span>
            <div className="grid grid-cols-2 gap-2">
              {details?.map((detail: any, index: number) => (
                <div className="last-of-type:odd:col-span-2">
                  <TextInput
                    type="text"
                    id={`offer_details.${index}.value`}
                    label={detail?.keydetail}
                    placeholder={`${detail?.keydetail}`}
                    required={
                      !detail?.required ? false : detail?.required === "Yes"
                    }
                    {...form}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <div className="pt-6">
        <span className="text-xs font-light">Offer Details</span>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <TextInput
              type="text"
              id="policy_number"
              label="Policy Number"
              placeholder="e.g. SGS-11288/12334"
              required={true}
              {...form}
            />
          </div>
          <div>
            <TextInput
              type="text"
              id="insured_by"
              label="Insured"
              placeholder="e.g. "
              required={true}
              {...form}
            />
          </div>
          <div>
            <TextInput
              type="text"
              id="sum_insured"
              label="Sum Insured"
              placeholder="e.g. "
              required={true}
              {...form}
            />
          </div>
          <div>
            <TextInput
              type="text"
              id="premium"
              label="Premium"
              placeholder="e.g. 1200"
              required={true}
              {...form}
            />
          </div>
          <div>
            <TextInput
              type="text"
              id="rate"
              label="Rate (%)"
              placeholder="e.g. 12.5"
              disabled={true}
              required={true}
              {...form}
            />
          </div>
          <div>
            <TextInput
              type="text"
              id="facultative_offer"
              label="Facultative Offer (%)"
              placeholder="e.g. 12.5"
              required={true}
              {...form}
            />
          </div>
          <div>
            <TextInput
              type="text"
              id="commission"
              label="Commission (%)"
              placeholder="e.g. 12.5"
              required={true}
              {...form}
            />
          </div>
          <div>
            <TextInput
              type="text"
              id="brokerage"
              label="Preliminary Brokerage (%)"
              placeholder="e.g. 12.5"
              required={true}
              {...form}
            />
          </div>
          <div className={""}>
            <CurrencyPicker
              id="currency"
              label="Currency"
              placeholder="e.g. USD"
              required={true}
              {...form}
            />
          </div>
          <FeatureWrap domains={["KEK"]}>
            <div className={""}>
              <TextInput
                id="co_insurance_share"
                label="Co-Insurance Share (%)"
                placeholder="e.g. 12.5"
                required={false}
                {...form}
              />
            </div>
            <div>
              <RegularSelectInput
                id="payment_type"
                label="Payment Type"
                placeholder="e.g. 12.5"
                required={true}
                options={[
                  {
                    value: "payable_in_full",
                    label: "Payable in Full"
                  },
                  {
                    value: "instalment",
                    label: "Instalment"
                  }
                ]}
                {...form}
              />
            </div>
            {form.values.payment_type === "instalment" && (
              <div className="">
                <TextInput
                  type="number"
                  id="no_of_installments"
                  label="No of Installments"
                  placeholder="e.g. 12"
                  required={true}
                  min={1}
                  step={1}
                  {...form}
                />
                {form?.errors?.no_of_installments && !form?.touched?.no_of_installments && (
                  <p className="mt-2 text-sm text-red-600">
                    {form?.errors?.no_of_installments?.toString()}
                  </p>
                )}
              </div>
            )}

          </FeatureWrap>
        </div>
      </div>
      <FeatureWrap domains={["KEK"]}>
        <div className="pt-6">
          <span className="text-xs font-light">Exchange Rate</span>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2 mt-3">
              <SwitchInput
                id="addExchangeRate"
                label="Add Exchange Rate"
                description="Toggle to add an exchange rate."
                {...form}
              />
            </div>
            <Show if={form.values.addExchangeRate}>
              <>
                <div>
                  <CurrencyPicker
                    id="ex_currency"
                    label="Currency"
                    placeholder="e.g. USD"
                    required={true}
                    {...form}
                  />
                </div>
                <div>
                  <TextInput
                    type="text"
                    id="ex_rate"
                    label="Exchange Rate"
                    placeholder="e.g. 1.2"
                    required={true}
                    {...form}
                  />
                </div>
              </>
            </Show>
          </div>
        </div>
      </FeatureWrap>
      <div className="pt-6">
        <span className="text-xs font-light">Period</span>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <TextInput
              type="date"
              id="period_of_insurance_from"
              label="From"
              placeholder=""
              required={true}
              {...form}
            />
          </div>
          <div>
            <TextInput
              type="date"
              id="period_of_insurance_to"
              label="To"
              required={true}
              {...form}
            />
          </div>
          <div className="col-span-2 mt-6">
            <RichEditor
              id="comment"
              label="Comment"
              placeholder="e.g. Comment goes here ..."
              {...form}
            />
          </div>
          <FeatureWrap domains={["KEK"]}>
            <div className="col-span-2 mt-6">
              <SwitchInput
                id="include_nkrol_comment"
                label="NKROL Information"
                description="Include NKORL information to placing slip"
                {...form}
              />
            </div>
            <Show if={form.values.include_nkrol_comment}>
              <div className="col-span-2 mt-6">
                <RichEditor
                  id="information_comment"
                  label="NKORL Information"
                  placeholder="e.g. Comment goes here ..."
                  {...form}
                />
              </div>
            </Show>
          </FeatureWrap>
        </div>
      </div>
    </div>
  );
};

export default OfferForm;
